import React, { useEffect } from 'react'
import styled from 'styled-components'
import { FormError, Input, InputLabel as Label, Button } from '../../../Common'
import { useForm, Controller, useWatch } from 'react-hook-form'
import { useToasts } from 'react-toast-notifications'
import {
  DefaultFormErrors,
  useErrors,
  useLocalLabels,
  useChangeProfilePassword,
} from '@sentecacommerce-theme/lib'
import { ProfileLabels } from '../../profile.props'

export const UpdatePassword = () => {
  const { addToast } = useToasts()
  const { updatePassword } = useLocalLabels<ProfileLabels>()

  const [mutate, { isLoading, error }] = useChangeProfilePassword({
    onSuccess: () => {
      reset()
      addToast(updatePassword?.validations?.passChangeSuccess, {
        appearance: 'success',
        autoDismiss: true,
      })
    },
  })

  const onSubmit = async (data: {
    currentPassword: string
    newPassword: string
  }) => {
    mutate({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    })
  }

  const apiErrors = useErrors<DefaultFormErrors>(error, {
    default: updatePassword?.validations?.somethingWentWrong,
  })

  const { handleSubmit, errors, control, reset } = useForm({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      newPasswordConfirm: '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const currentPassword = useWatch({
    control,
    name: 'currentPassword',
    defaultValue: '',
  })

  const newPassword = useWatch({
    control,
    name: 'newPassword',
    defaultValue: '',
  })

  const validatePasswordMatch = (passwordConfirm?: string) => {
    return passwordConfirm === newPassword
      ? undefined
      : updatePassword?.validations.passwordsDoNotMatch
  }

  const validateNewPassword = (newPasswrd?: string) => {
    return newPasswrd === currentPassword
      ? updatePassword?.validations.newPassMustBeDifferent
      : undefined
  }

  const buttonDisabled = isLoading || Boolean(Object.keys(errors).length)

  useEffect(() => {
    if (apiErrors) {
      addToast(apiErrors?.default, {
        appearance: 'error',
        autoDismiss: true,
      })
    }
  }, [error])

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <InputWrapper>
        <Label htmlFor="update-password-current" required>
          {updatePassword?.current}
          <Controller
            control={control}
            name="currentPassword"
            rules={{
              required: updatePassword?.validations.mandatoryField,
            }}
            as={
              <Input
                id="update-password-current"
                type="password"
                name="currentPassword"
              />
            }
          />
          <FormError errors={errors} fieldName="currentPassword" />
        </Label>

        <Label htmlFor="update-password-new" required>
          {updatePassword?.newPass}
          <Controller
            control={control}
            name="newPassword"
            rules={{
              required: updatePassword?.validations.mandatoryField,
              minLength: {
                value: 8,
                message: updatePassword?.validations.passMustBeLonger,
              },
              validate: validateNewPassword,
            }}
            as={
              <Input
                id="update-password-new"
                type="password"
                name="newPassword"
              />
            }
          />
          <FormError errors={errors} fieldName="newPassword" />
        </Label>

        <Label htmlFor="update-password-confirm" required>
          {updatePassword?.repeatPassword}
          <Controller
            control={control}
            name="newPasswordConfirm"
            rules={{
              required: updatePassword?.validations.mandatoryField,
              minLength: {
                value: 8,
                message: updatePassword?.validations.passMustBeLonger,
              },
              validate: validatePasswordMatch,
            }}
            as={
              <Input
                id="update-password-confirm"
                type="password"
                name="newPasswordConfirm"
              />
            }
          />
          <FormError errors={errors} fieldName="newPasswordConfirm" />
        </Label>
      </InputWrapper>

      <SubmitWrapper>
        <Button
          type="submit"
          disabled={buttonDisabled}
          className="ml-4"
          variant={buttonDisabled ? 'disabled' : 'green'}
          borderRadius="small"
        >
          {updatePassword?.save}
        </Button>
      </SubmitWrapper>
    </form>
  )
}

const InputWrapper = styled.div.attrs({
  className: 'mt-1 mb-2 pb-1',
})`
  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    width: 52.5%;
  }
`

const SubmitWrapper = styled.div.attrs({
  className: 'mt-5 pt-5',
})`
  border-top: 1px solid ${({ theme }) => theme.palette.lightgrey};
  display: flex;
  justify-content: flex-end;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    & button {
      width: 21% !important;
    }
  }
`
